import type { RootState } from '@reducers/index';
import type { SearchState } from '@reducers/search';
import { CategoryID } from '@sbt-web/network/types';
import { getGeoLevel, SeoMetaPayload, TuttaItalia } from '@sbt-web/networking';
import { formatDistance } from '@sbt-web/utils';
import classnames from 'classnames';
import { connect } from 'react-redux';
import React from 'react';
import { getVehiclesLabel } from '../helpers/automotive';
import { capitalizeFirst, capitalizeOnlyFirst } from '../helpers/common';
import { getGeoLabel, SEOTag } from '../helpers/geo';
import AdsCount from './AdsCount';
import ownClasses from './ListingHeading.module.scss';

interface StateProps {
  search: SearchState;
  totalAds: number;
}

interface OwnProps {
  seo?: SeoMetaPayload;
}

type Props = OwnProps & StateProps;

function buildText(search: SearchState, seo?: SeoMetaPayload): string {
  if (seo?.h1) {
    return seo.h1;
  }
  const adsLabel = search.query || 'Annunci';

  let geoLabel: string;
  if (search.radiusSearch == null) {
    const geoLevel = getGeoLevel(search.geo, search.searchNearRegions);
    geoLabel = getGeoLabel(search, geoLevel, SEOTag.H1);
  } else {
    const distanceLabel = formatDistance(search.radiusSearch.radiusMeters);

    const reference =
      search.radiusSearch.friendlyName != null
        ? `da ${search.radiusSearch.friendlyName}`
        : 'dal punto che hai selezionato';

    geoLabel = `entro ${distanceLabel} ${reference}`;
  }

  const vehiclesLabel = getVehiclesLabel(search);
  const categoryLabel = capitalizeOnlyFirst(getCategoryLabel(search));

  const categoriesToCapitalize: CategoryID[] = [
    CategoryID.Auto,
    CategoryID.Moto,
  ];
  if (vehiclesLabel && categoriesToCapitalize.includes(search.category.id)) {
    return capitalizeFirst(`${vehiclesLabel} ${geoLabel}`);
  }

  if (search.category.id === CategoryID.Tutte) {
    return capitalizeFirst(`${adsLabel} ${geoLabel}`);
  } else if (search.geo.region === TuttaItalia && search.radiusSearch == null) {
    return capitalizeFirst(`${adsLabel} in ${categoryLabel}`);
  } else {
    return capitalizeFirst(`${adsLabel} in ${categoryLabel} ${geoLabel}`);
  }
}

function getCategoryLabel(search: SearchState): string {
  if (search.category.id === CategoryID.Tutte || !search.category.label) {
    return '';
  }
  return search.category.label;
}

function ListingHeadingComponent({ totalAds, search, seo }: Props) {
  return (
    <div
      className={classnames('listing-heading', ownClasses['listing-heading'])}
    >
      <AdsCount>{totalAds}</AdsCount>
      <h1 className="headline-6">
        {buildText(search, seo).trim().replace(/\s\s+/g, ' ')}
      </h1>
    </div>
  );
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  search: state.search,
  seo: ownProps.seo,
  totalAds: state.items.total,
});

const ListingHeading = connect(mapStateToProps)(ListingHeadingComponent);

export { ListingHeading, ListingHeadingComponent };
